import React, { useState, useEffect, useMemo } from 'react'
import { Ripple } from '../utils/PopupRipple.jsx';
import Select from "../utils/Select.jsx";
import { useTranslation } from 'react-i18next';
import { FaInfoCircle, FaExclamationTriangle, FaClock, FaHourglassHalf } from 'react-icons/fa';
import './withdraw.scss';
import { toast } from "react-toastify";
import { useAccountManagerContext } from '../../contexts/Account-Manager-Context';
import {
    API_ENDPOINT_MAKE_WITHDRAW,
    API_ENDPOINT_CALCULATE_WITHDRAWAL_PENALTY,
    API_ENDPOINT_WITHDRAWAL_ELIGIBILITY,
} from '../../data/Endpoints-API';
import APIMiddleware from '../../data/api/Api-Middleware';
import { useAuthContext } from '../../contexts/Auth-Context';
import { useChartContext } from '../../contexts/Chart-Context';
import { useMetricsContext } from '../../contexts/Metrics-Context';
import Spinner from '../spinner/Spinner';
import { modalSelectStyles } from '../../utils/dropdownOptionStyles.js';

const PENALTY_INFO = {
    penaltyFree:
        'This is the portion of your withdrawal that is not subject to any penalties.',
    penaltyAmount:
        'This amount is deducted as a penalty based on the withdrawal rules.',
};

const PenaltyInfoTip = ({ title }) => (
    <span className="penalty-info-tip" tabIndex={0} aria-label={title}>
        <FaInfoCircle className="penalty-info-tip__icon" aria-hidden="true" />
        <span className="penalty-info-tip__bubble" role="tooltip">{title}</span>
    </span>
);

const WITHDRAWAL_TYPES = {
    BANK_TRANSFER: 'bank_transfer',
    CRYPTO: 'crypto',
};

const withdrawalTypeOptions = (t) => [
    { value: WITHDRAWAL_TYPES.BANK_TRANSFER, label: t('modals.bankTransfer') },
    { value: WITHDRAWAL_TYPES.CRYPTO, label: t('modals.crypto') },
];

const coinOptions = [
    { value: 'BTC', label: 'BTC' },
    { value: 'LTC', label: 'LTC' },
];

const formatMoney = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const WithdrawModal = () => {
    const { t } = useTranslation();
    const { setShowWithdraw } = useAccountManagerContext();
    const { user } = useAuthContext();
    const { metrics } = useMetricsContext();
    const { selectedStyle } = useChartContext();

    const currencySymbol = metrics?.userCurrencySymbol || user?.userCurrencySymbol || '';
    const currencyName = metrics?.userCurrencyName || user?.userCurrencyName || '';
    
    const formatCurrencyAmount = (value) =>
      currencySymbol ? `${currencySymbol} ${formatMoney(value)}` : formatMoney(value);

    const [step, setStep] = useState('form');
    const [penaltyPreview, setPenaltyPreview] = useState(null);
    const [amount, setAmount] = useState('');
    const typeOptions = useMemo(() => withdrawalTypeOptions(t), [t]);
    const [withdrawalType, setWithdrawalType] = useState(null);
    const activeWithdrawalType = withdrawalType ?? typeOptions[0];

    useEffect(() => {
        setWithdrawalType((prev) => typeOptions.find((o) => o.value === prev?.value) ?? typeOptions[0]);
    }, [typeOptions]);
    const [selectedCoin, setSelectedCoin] = useState(coinOptions[1]);
    const [walletAddress, setWalletAddress] = useState('');
    const [beneficiaryName, setBeneficiaryName] = useState('');
    const [beneficiarySurname, setBeneficiarySurname] = useState('');
    const [beneficiaryAddress, setBeneficiaryAddress] = useState('');
    const [iban, setIban] = useState('');
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [eligibilityLoading, setEligibilityLoading] = useState(true);
    const [eligibility, setEligibility] = useState(null);

    const isCrypto = activeWithdrawalType.value === WITHDRAWAL_TYPES.CRYPTO;

    const totalWithDrawalableBalance = Math.max(
        0,
        parseFloat(metrics?.totalWithDrawalableBalance) || 0
    );
    const withdrawalEligibleBalance = parseFloat(eligibility?.withdrawal_eligible_balance) || 0;
    const isWithdrawRequestPending = Boolean(eligibility?.is_withdraw_request_pending);
    const withdrawalEligibilityDays = eligibility?.withdrawal_eligibility_days || 30;

    const disableForm =
        eligibilityLoading ||
        totalWithDrawalableBalance <= 0 ||
        (totalWithDrawalableBalance > 0 && withdrawalEligibleBalance <= 0) ||
        isWithdrawRequestPending;

    useEffect(() => {
        let cancelled = false;

        const fetchEligibility = async () => {
            if (!user?.userId) {
                setEligibilityLoading(false);
                return;
            }

            try {
                setEligibilityLoading(true);
                const response = await APIMiddleware.get(
                    API_ENDPOINT_WITHDRAWAL_ELIGIBILITY(user.userId)
                );

                if (!cancelled && response?.status === 'success') {
                    setEligibility(response);
                }
            } catch (error) {
                if (!cancelled) {
                    const message =
                        error?.response?.data?.message ||
                        'Unable to load withdrawal eligibility. Please try again.';
                    toast.error(message, { position: 'top-right' });
                }
            } finally {
                if (!cancelled) {
                    setEligibilityLoading(false);
                }
            }
        };

        fetchEligibility();

        return () => {
            cancelled = true;
        };
    }, [user?.userId]);

    const getSelectStyles = (width = '150px') => modalSelectStyles(width);

    const validateForm = () => {
        const errors = [];

        if (!amount || Number(amount) <= 0) {
            errors.push(t('modals.errors.withdrawalAmountInvalid'));
        }

        if (isCrypto) {
            if (!selectedCoin) {
                errors.push(t('modals.errors.coinRequired'));
            }
            if (!walletAddress) {
                errors.push(t('modals.errors.walletAddressRequired'));
            }
        } else {
            if (!beneficiaryName) {
                errors.push(t('modals.errors.beneficiaryNameRequired'));
            }
            if (!beneficiarySurname) {
                errors.push(t('modals.errors.beneficiarySurnameRequired'));
            }
            if (!beneficiaryAddress) {
                errors.push(t('modals.errors.beneficiaryAddressRequired'));
            }
            if (!iban) {
                errors.push(t('modals.errors.ibanRequired'));
            }
        }

        return errors;
    };

    const buildWithdrawPayload = () => {
        const payload = {
            amount,
            user_detail_id: user.userId,
            userName: user.userName,
            withdrawal_type: activeWithdrawalType.value,
            comment: comment || undefined,
        };

        if (isCrypto) {
            payload.coin = selectedCoin.value;
            payload.address = walletAddress;
        } else {
            payload.beneficiary_name = beneficiaryName;
            payload.beneficiary_surname = beneficiarySurname;
            payload.beneficiary_address = beneficiaryAddress;
            payload.iban = iban;
        }

        return payload;
    };

    const handleClose = () => {
        setShowWithdraw(false);
        setStep('form');
        setPenaltyPreview(null);
    };

    const handlePreviewSubmit = async () => {
        if (disableForm) {
            return;
        }

        const errors = validateForm();

        if (errors.length > 0) {
            errors.forEach((error) => {
                toast.error(error, { position: 'top-right' });
            });
            return;
        }

        try {
            setCalculating(true);

            const response = await APIMiddleware.post(
                API_ENDPOINT_CALCULATE_WITHDRAWAL_PENALTY(),
                {
                    user_detail_id: user.userId,
                    amount,
                }
            );

            if (response?.status === 'success') {
                setPenaltyPreview(response);
                setStep('confirm');
            } else {
                toast.error(response?.message || 'Calculation failed.', {
                    position: 'top-right',
                });
            }
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.message ||
                'An error occurred while calculating the penalty.';
            toast.error(message, { position: 'top-right' });
        } finally {
            setCalculating(false);
        }
    };

    const handleConfirmWithdraw = async () => {
        if (disableForm) {
            return;
        }

        const errors = validateForm();

        if (errors.length > 0) {
            errors.forEach((error) => {
                toast.error(error, { position: 'top-right' });
            });
            setStep('form');
            return;
        }

        try {
            setLoading(true);

            const response = await APIMiddleware.post(
                API_ENDPOINT_MAKE_WITHDRAW(),
                buildWithdrawPayload()
            );

            const message =
                response?.message ||
                response?.data?.message ||
                'Withdraw request submitted successfully!';

            toast.success(message, { position: 'top-right' });
            handleClose();
        } catch (error) {
            if (error?.response?.status === 422 && error?.response?.data?.errors) {
                const allErrorMessages = Object.keys(error.response.data.errors)
                    .filter(key => Array.isArray(error.response.data.errors[key]))
                    .map(key => error.response.data.errors[key].join(' '))
                    .join('\n');

                toast.error(allErrorMessages || 'Validation failed.', {
                    position: 'top-right',
                });
            } else if (error?.response?.status === 400 && error?.response?.data?.errors?.message) {
                toast.error(error?.response?.data?.errors?.message || 'An unknown error occurred', {
                    position: 'top-right',
                });
            } else if (error?.response?.status === 400 && error?.response?.data?.message) {
                toast.error(error.response.data.message || 'An unknown error occurred', {
                    position: 'top-right',
                });
            } else {
                toast.error(error?.response?.data?.message || 'An unknown error occurred', {
                    position: 'top-right',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const renderWithdrawBanners = () => {
        if (eligibilityLoading) {
            return (
                <div className="withdraw-alert withdraw-alert--loading">
                    Checking withdrawal eligibility...
                </div>
            );
        }

        return (
            <div className="withdraw-alert-section">
                {totalWithDrawalableBalance <= 0 && (
                    <div className="withdraw-alert withdraw-alert--warning">
                        <FaExclamationTriangle className="withdraw-alert__icon" aria-hidden="true" />
                        <span>
                            Your balance is zero. You cannot submit a withdrawal request.
                        </span>
                    </div>
                )}

                {totalWithDrawalableBalance > 0 && withdrawalEligibleBalance <= 0 && (
                    <div className="withdraw-alert withdraw-alert--warning">
                        <FaClock className="withdraw-alert__icon" aria-hidden="true" />
                        <span>
                            Your account has a balance, but the funds are not yet eligible for withdrawal.
                            Please note that you can only withdraw amounts that have been in your account for at
                            least <strong>{withdrawalEligibilityDays} days</strong> from the date of deposit.
                            Check back later when your funds meet this requirement.
                        </span>
                    </div>
                )}

                {isWithdrawRequestPending && (
                    <div className="withdraw-alert withdraw-alert--warning">
                        <FaHourglassHalf className="withdraw-alert__icon" aria-hidden="true" />
                        <span>
                            {t('modals.pendingWithdrawRequest')}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const renderPenaltySummary = () => (
        <div className="penalty-summary">
            <p className="penalty-summary__intro">
                {t('modals.reviewPenaltyBreakdown')}
            </p>

            <div className="penalty-summary__grid">
                <div className="penalty-stat">
                    <span className="penalty-stat__label">{t('modals.requestedAmount')}</span>
                    <span className="penalty-stat__value">{formatCurrencyAmount(amount)}</span>
                </div>
                <div className="penalty-stat penalty-stat--positive">
                    <span className="penalty-stat__label">
                        {t('modals.penaltyFreeLabel')}
                        <PenaltyInfoTip title={PENALTY_INFO.penaltyFree} />
                    </span>
                    <span className="penalty-stat__value">{formatCurrencyAmount(penaltyPreview?.penalty_free_amount)}</span>
                </div>
                <div className="penalty-stat penalty-stat--negative">
                    <span className="penalty-stat__label">
                        {t('modals.penaltyLabel')}
                        <PenaltyInfoTip title={PENALTY_INFO.penaltyAmount} />
                    </span>
                    <span className="penalty-stat__value">{formatCurrencyAmount(penaltyPreview?.penalty_amount)}</span>
                </div>
                <div className="penalty-stat">
                    <span className="penalty-stat__label">{t('modals.profit')}</span>
                    <span className="penalty-stat__value">{formatCurrencyAmount(penaltyPreview?.profit)}</span>
                </div>
            </div>

            <div className="penalty-summary__net">
                <div className="penalty-summary__net-copy">
                    <span className="penalty-summary__net-label">{t('modals.youWillReceive')}</span>
                    <span className="penalty-summary__net-hint">{t('modals.afterPenalty')}</span>
                </div>
                <span className="penalty-summary__net-value">{formatCurrencyAmount(penaltyPreview?.amount)}</span>
            </div>
        </div>
    );

    return (
        <div className="confirm-box-2" role='true'>
            <div className={`card withdraw-modal-card ${step === 'confirm' ? 'withdraw-modal-card--confirm' : ''}`}>
                <h2>
                    {step === 'confirm' ? t('modals.withdrawPenaltyTitle') : t('modals.withdrawTitle')}
                </h2>

                <div className={`withdraw-form-body ${step === 'confirm' ? 'withdraw-form-body--confirm' : ''}`}>
                    {step === 'form' ? (
                        <>
                            {renderWithdrawBanners()}

                            <div className='input-div-1'>
                                <div className='withdraw-field'>
                                    <div className='mt-5 mb-5'>
                                        <label className='text-color'>{t('modals.amountLabel', { currency: currencyName })}</label>
                                    </div>
                                    <input
                                        className='amountInput'
                                        type="number"
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder={t('modals.enterAmountPlaceholder')}
                                        value={amount}
                                        disabled={disableForm}
                                    />
                                </div>
                                <div className='withdraw-field'>
                                    <div className='mt-5 mb-5'>
                                        <label className='text-color'>{t('modals.typeLabel')}</label>
                                    </div>
                                    <Select
                                        options={typeOptions}
                                        value={activeWithdrawalType}
                                        onChange={setWithdrawalType}
                                        styles={getSelectStyles('100%')}
                                        isSearchable={false}
                                        isDisabled={disableForm}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                            </div>

                            {isCrypto ? (
                                <>
                                    <div className='input-div-1'>
                                        <div className='withdraw-field'>
                                            <div className='mt-5 mb-5'>
                                                <label className='text-color'>Coin:</label>
                                            </div>
                                            <Select
                                                options={coinOptions}
                                                value={selectedCoin}
                                                onChange={setSelectedCoin}
                                                styles={getSelectStyles('100%')}
                                                isSearchable={false}
                                                isDisabled={disableForm}
                                                menuPortalTarget={document.body}
                                            />
                                        </div>
                                    </div>
                                    <div className='my-15'>
                                        <div className='mt-5 mb-5'>
                                            <label className='text-color'>{t('modals.walletAddress')}</label>
                                        </div>
                                        <input
                                            className='amountInput'
                                            type="text"
                                            onChange={(e) => setWalletAddress(e.target.value)}
                                            placeholder={t('modals.walletPlaceholder')}
                                            value={walletAddress}
                                            disabled={disableForm}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className='input-div-1'>
                                        <div className='withdraw-field'>
                                            <div className='mt-5 mb-5'>
                                                <label className='text-color'>{t('modals.beneficiaryNameLabel')}</label>
                                            </div>
                                            <input
                                                className='amountInput'
                                                type="text"
                                                onChange={(e) => setBeneficiaryName(e.target.value)}
                                                placeholder={t('modals.beneficiaryNamePlaceholder')}
                                                value={beneficiaryName}
                                                disabled={disableForm}
                                            />
                                        </div>
                                        <div className='withdraw-field'>
                                            <div className='mt-5 mb-5'>
                                                <label className='text-color'>{t('modals.beneficiarySurnameLabel')}</label>
                                            </div>
                                            <input
                                                className='amountInput'
                                                type="text"
                                                onChange={(e) => setBeneficiarySurname(e.target.value)}
                                                placeholder={t('modals.beneficiarySurnamePlaceholder')}
                                                value={beneficiarySurname}
                                                disabled={disableForm}
                                            />
                                        </div>
                                    </div>
                                    <div className='my-15'>
                                        <div className='mt-5 mb-5'>
                                            <label className='text-color'>{t('modals.beneficiaryAddressLabel')}</label>
                                        </div>
                                        <input
                                            className='amountInput'
                                            type="text"
                                            onChange={(e) => setBeneficiaryAddress(e.target.value)}
                                            placeholder={t('modals.beneficiaryAddressPlaceholder')}
                                            value={beneficiaryAddress}
                                            disabled={disableForm}
                                        />
                                    </div>
                                    <div className='my-15'>
                                        <div className='mt-5 mb-5'>
                                            <label className='text-color'>{t('modals.ibanLabel')}</label>
                                        </div>
                                        <input
                                            className='amountInput'
                                            type="text"
                                            onChange={(e) => setIban(e.target.value)}
                                            placeholder={t('modals.ibanPlaceholder')}
                                            value={iban}
                                            disabled={disableForm}
                                        />
                                    </div>
                                </>
                            )}

                            <div className='my-15'>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color'>{t('modals.commentOptional')}</label>
                                </div>
                                <textarea
                                    className='withdraw-comment-input'
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder={t('modals.commentPlaceholder')}
                                    value={comment}
                                    rows={2}
                                    disabled={disableForm}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="penalty-summary-wrap">
                            {renderPenaltySummary()}
                        </div>
                    )}
                </div>

                <div className="row-scss">
                    {step === 'form' ? (
                        <>
                            <button
                                onClick={handleClose}
                                style={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    isolation: 'isolate',
                                    backgroundColor: selectedStyle.sellColor,
                                }}
                            >
                                <Ripple />
                                {t('modals.cancel')}
                            </button>
                            <div className="blank"></div>
                            {!disableForm && (
                                <button
                                    onClick={handlePreviewSubmit}
                                    disabled={calculating}
                                    style={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        isolation: 'isolate',
                                        backgroundColor: selectedStyle.buyColor,
                                    }}
                                >
                                    <Ripple />
                                    {calculating ? <Spinner /> : t('modals.submit')}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setStep('form')}
                                disabled={loading}
                                style={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    isolation: 'isolate',
                                    backgroundColor: selectedStyle.sellColor,
                                }}
                            >
                                <Ripple />
                                Back
                            </button>
                            <div className="blank"></div>
                            <button
                                onClick={handleConfirmWithdraw}
                                disabled={loading}
                                style={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    isolation: 'isolate',
                                    backgroundColor: selectedStyle.buyColor,
                                }}
                            >
                                <Ripple />
                                {loading ? <Spinner /> : t('modals.confirmWithdrawal')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WithdrawModal;
